import React, { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import CustomHumanChart from "../CustomHumanChart";
import CustomeMetrics from "../CustomeMetrixChart";
import { ChartGridType, chartType, ChartViewType, IDynamicChartsProps } from "src/pages/Insights/models/interfaces";
import { QuantComponentsRenderedFromType } from "src/pages/NewAskKural/models/interfaces";
import { sortByValuesAndThenByMax } from "src/pages/Insights/transformer";
import LineCharts from "./Charts/LineChart";
import CustomBarChart from "./Charts/BarChart";
import { Empty, Tooltip as TooltipEllips } from "src/assets/imports/antdComponents";
import "./style.css";
import { formatValue } from "src/pages/Insights/transformer";

const DynamicCharts: React.FC<IDynamicChartsProps> = React.memo(
	({
		obj = {},
		type,
		config,
		isMini,
		legends = [],
		showYAxisLabel = true,
		showXAxis = true,
		maxDomain = "auto",
		minDomain = "auto",
		chartSegmentLength = 1,
		isSectionalSummary,
		renderedFrom = QuantComponentsRenderedFromType.Quant,
		viewType = ChartViewType.Dashboard,
		grid = ChartGridType.oneXone,
	}) => {
		const { colors, n_size_value = [] } = obj;
		const bottomMargin = obj?.xAxisLables ? (obj?.graph_customization?.legend_pos !== "Top" ? 10 : 50) : obj?.graph_customization?.legend_pos !== "Top" ? 50 : 50;

		const data2 =
			legends?.length > 0
				? obj?.chartData?.map((item) => {
						return Object.fromEntries(Object.entries(item).filter(([key]) => !legends.includes(key)));
				  })
				: obj?.chartData;

		//const data = type?.toLowerCase() === chartType.Bar?.toLocaleLowerCase() && config.layout === "vertical" ? sortByValuesAndThenByMax(data2) : data2;
		const data =
			type?.toLowerCase() === chartType.Bar?.toLocaleLowerCase() && config.layout === "vertical" ? [...(data2 || [])].sort((a, b) => a?.name?.localeCompare(b?.name)) : data2;

		const renderChart = () => {
			if (data?.length === 0)
				return (
					<div className="flex justify-center items-center h-full align-middle">
						<Empty />
					</div>
				);
			switch (type?.toLowerCase()) {
				case chartType.Bar?.toLocaleLowerCase():
					return (
						<CustomBarChart
							wholeData={data}
							obj={obj}
							type={type}
							config={config}
							isMini={isMini}
							legends={legends}
							showYAxisLabel={showYAxisLabel}
							showXAxis={showXAxis}
							maxDomain={maxDomain}
							minDomain={minDomain}
							chartSegmentLength={chartSegmentLength}
							isSectionalSummary={isSectionalSummary}
							renderedFrom={renderedFrom}
							viewType={viewType}
							grid={grid}
						/>
					);
				case chartType.Line_Horizontal?.toLocaleLowerCase():
					return (
						<LineCharts
							wholeData={data}
							obj={obj}
							type={type}
							config={config}
							isMini={isMini}
							legends={legends}
							showYAxisLabel={showYAxisLabel}
							showXAxis={showXAxis}
							maxDomain={maxDomain}
							minDomain={minDomain}
							chartSegmentLength={chartSegmentLength}
							isSectionalSummary={isSectionalSummary}
							renderedFrom={renderedFrom}
							viewType={viewType}
							grid={grid}
						/>
					);
				case chartType.Line_Vertical?.toLocaleLowerCase():
					return (
						<LineCharts
							wholeData={data}
							obj={obj}
							type={type}
							config={config}
							isMini={isMini}
							legends={legends}
							showYAxisLabel={showYAxisLabel}
							showXAxis={showXAxis}
							maxDomain={maxDomain}
							minDomain={minDomain}
							chartSegmentLength={chartSegmentLength}
							isSectionalSummary={isSectionalSummary}
							renderedFrom={renderedFrom}
							viewType={viewType}
							grid={grid}
						/>
					);
				case chartType.Pie?.toLocaleLowerCase():
					return (
						<PieChart width={config.width} height={config.height}>
							<Tooltip />
							{config.showLegends && <Legend />}
							<Pie data={data} dataKey="value" nameKey="name" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
								{data.map((entry, index) => (
									<Cell key={`cell-${index}`} fill={colors?.[index % colors?.length]} />
								))}
							</Pie>
						</PieChart>
					);
				case chartType.Human_Chart?.toLocaleLowerCase():
					return <CustomHumanChart data={obj} />;
				case chartType.Metrics?.toLocaleLowerCase():
					return (
						// <div style={{ height: 540 }}>
						<CustomeMetrics data={obj} />
						// </div>
					);
				case chartType.Histogram_Chart?.toLocaleLowerCase():
					return (
						<ResponsiveContainer
							className="recharts-wrapper"
							style={{ position: "absolute", right: 25 }} // Make position absolute here
						>
							<BarChart barCategoryGap={config?.barCategoryGap ?? "0%"} barGap={config?.barGap ?? 0} data={data} margin={{ top: 20, right: 0, left: 0, bottom: 0 }}>
								<XAxis
									//stroke={`var(--neutral-grey-opacity-10)`} strokeWidth={1}
									dataKey="label"
									stroke={`var(--neutral-grey-opacity-10)`}
									tickLine={true}
									tick={{ fontSize: 7 }}
									strokeWidth={1}
								/>
								<YAxis tickLine={true} stroke={`var(--neutral-grey-opacity-10)`} tick={{ fontSize: 7 }} strokeWidth={1} />
								{config.showTooltip && <Tooltip wrapperStyle={{ fontSize: 10 }} />}
								<Bar dataKey="count" fill="#2EB8BA" />
							</BarChart>
						</ResponsiveContainer>
					);
				case chartType.MultiColor_Histogram_Chart?.toLocaleLowerCase():
					const maxLabelSpace = Math.max(
						...data.map(
							(entry) => `${entry.count} (${entry.percentage}%)`.length * 4 // Estimate 4px per character
						)
					);
					return (
						<ResponsiveContainer className="recharts-wrapper" style={{ position: "absolute", right: 25 }}>
							<BarChart
								barCategoryGap={config?.barCategoryGap ?? "5%"}
								barGap={config?.barGap ?? 0}
								data={data}
								margin={{ top: 20, right: 20 + maxLabelSpace, left: 0, bottom: 0 }}
								layout="vertical"
								width={config?.width ?? "100%"}
								height={config?.height}>
								{/* For horizontal layout: YAxis shows categories, XAxis shows values */}
								<XAxis type="number" tickLine={false} stroke={`var(--neutral-grey-opacity-10)`} tick={false} strokeWidth={1} />
								<YAxis
									type="category"
									dataKey="label"
									stroke={`var(--neutral-grey-opacity-10)`}
									tickLine={true}
									tick={{ fontSize: config?.yAisFontSize || 10, fontWeight: 600, fill: "#112E65" }}
									//tick={<CustomYAxisTick />}
									tickFormatter={(value) => {
										if (config?.maxYaxisChar) {
											return value.length > config?.maxYaxisChar ? value.substring(0, config?.maxYaxisChar) + "..." : value;
										}
										return value;
									}}
									className={"CountWidgetChart"}
									strokeWidth={1}
									width={config?.yLabelsWidth} // Give more space for labels
								/>

								{config.showTooltip &&
									(config.tooltipContent ? (
										<Tooltip content={config.tooltipContent} wrapperStyle={{ fontSize: 10 }} />
									) : (
										<Tooltip wrapperStyle={{ fontSize: 10 }} />
									))}

								<Bar
									dataKey="count"
									barSize={config?.barSize || 10}
									label={{
										position: "right",
										content: ({ x, y, width, height, index }) => {
											const currentData = data.chartData || data;
											const entry = currentData[index];

											return (
												<text
													x={Number(x) + Number(width) + 5}
													y={Number(y) + Number(height) / 2}
													dy={4}
													fill="#112E65"
													fontSize={10}
													fontWeight={400}
													className="text-[--secondary-color]">
													{entry.count} ({entry.percentage}%)
												</text>
											);
										},
									}}>
									{(data.chartData || data).map((entry, index) => {
										const colorValue = entry.color || "#2EB8BA";
										return <Cell key={`cell-${index}`} fill={colorValue} />;
									})}
								</Bar>
							</BarChart>
						</ResponsiveContainer>
					);

				default:
					return (
						<div className="flex justify-center h-full align-middle empty-center">
							<Empty />
						</div>
					);
			}
		};

		if (!data2)
			return (
				<div className="flex justify-center items-center w-full h-full align-middle empty-center">
					<Empty />
				</div>
			);

		return (
			<ResponsiveContainer height={config.height ?? 250} width={isMini || data?.length === 0 ? undefined : config.width ?? 600}>
				{renderChart()}
			</ResponsiveContainer>
		);
	}
);

export default DynamicCharts;
